import { useCallback, useMemo, useRef } from 'react';

import customNanoId from 'lib/nanoid/nanoid';
import ActivitySheet from '../../shared/sheet/activity-sheet';
import SubActivityView from '../../sub-activity/sub-activity-view';
import {
  useInteractiveAudio,
  useInteractiveModal,
} from '../shared/interactive-response-shared.hooks';
import { InteractiveResponseContainer } from '../interactive-response.style';
import {
  RESPONSE_FREE,
  ViewProps,
} from './settings/interactive-response.constants';
import InteractiveResponseComponentResponse from '../shared/interactive-response-component-response';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { getComponentSelectorId } from '../../shared/logic/get-component-selector-id';
import InteractiveSequenceLargeView from './interactive-sequence-large-view';
import InteractiveSequenceSmallView from './interactive-sequence-small-view';
import { useBatchAnswersContext } from '../../shared/batch-answers.context';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import {
  DragItem,
  DropItem,
} from '../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import ClassroomAnswerTabs, {
  CLASS_VIEW,
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
  SUGGESTED_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import InteractiveResponseClassResponses from 'approot/activity-builder/classroom/interactive-response/interactive-response-class-responses';
import InteractiveResponseIndividualResponses from 'approot/activity-builder/classroom/interactive-response/interactive-response-individual-responses';
import InteractiveResponseSequencePreviewAnswer from './play/interactive-response-sequence-preview-answers';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import ClassroomAwaitingResponse from 'approot/activity-builder/classroom/classroom-awaiting';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../../shared/sheet/activity-sheet.style';
import { getSubActivityInstructionAsText } from '../../sub-activity/sub-activity.utils';
import InteractiveResponseSequenceAnswers from './interactive-response-sequence-answers';
import ActivitySheetSaveStatus from '../../shared/sheet/activity-sheet-save-status';
import { hasNoRichTextData } from '../../suggested-answers/view/suggested-answer-view.utils';
import InteractiveResponseSuggestedAnswers from 'approot/activity-builder/classroom/interactive-response/interactive-response-suggested-answers';

const InteractiveResponseView = (props: ViewProps) => {
  const { id, subActivityProperties, properties, suggestedAnswerProperties } =
    props;

  const isMediaUpSm = useMediaQuery(MEDIA_SM);
  const { isPlaying, startGame, finishGame } = useInteractiveModal();
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { isPublished, isLesson } = useActivityBuilderPresentContext();

  const canRespond = isPublished && activityBuilderRespondFlag;
  const isFreeResponse =
    properties.interactiveResponse.answerType === RESPONSE_FREE;

  const closeModalCallbackRef = useRef<Function | undefined>(undefined);

  const { tab, setTab } = useInitialClassroomTab({
    isVisible,
    canRespond,
    componentId: id,
  });

  const { toggleAudio, playAudio, playClick, playStart } =
    useInteractiveAudio();

  const { componentAnswers, variables, hasFetchedAnswers, error } =
    useTeacherGetAnswers({
      componentId: id,
      skip: !activityBuilderRespondFlag,
    });

  const { handleUpdate } = useBatchAnswersContext();

  const showSuggestedAnswers = useMemo(
    () => !isFreeResponse && !hasNoRichTextData(suggestedAnswerProperties),
    [isFreeResponse, suggestedAnswerProperties]
  );

  const handleSaveAttempt = useCallback(
    (dropItems: DropItem[], dragItems: DragItem[]) => {
      if (
        isPublished &&
        activityBuilderRespondFlag &&
        subActivityProperties?.activityComponentId &&
        variables
      ) {
        handleUpdate({
          variables,
          input: {
            __typename: 'ActivityAnswer',
            uuid: customNanoId(),
            activityId: subActivityProperties?.activityComponentId,
            componentId: id,
            createdAt: new Date(),
            updatedAt: null,
            studentId: null,
            answerReferences: null,
            answerType: 'interactive-response-answer',
            answer: {
              __typename: 'InteractiveResponseAnswer',
              interactiveResponseItems: dragItems.map(drag => {
                const dragItemDestination = dropItems.find(
                  drop => drop.zones[0].dragItemId === drag.id
                );
                return {
                  __typename: 'InteractiveResponseAnswerItem',
                  imageId: drag.question.image?.itemId || '',
                  dropZoneId: dragItemDestination?.id || '',
                  isCorrect: !!dragItemDestination?.zones[0].isCorrect,
                };
              }),
            },
          },
        });
      }
    },
    [
      id,
      subActivityProperties,
      variables,
      activityBuilderRespondFlag,
      isPublished,
      handleUpdate,
    ]
  );

  return (
    <>
      <SubActivityView subActivityDetails={props.subActivityProperties}>
        <InteractiveResponseContainer id={getComponentSelectorId(id)}>
          <InteractiveResponseSequenceAnswers
            {...props}
            isLoading={canRespond && !hasFetchedAnswers}
            answerAttempt={componentAnswers?.[0]}
            showAnswer={isLesson}
          />
          <InteractiveResponseComponentResponse
            id={id}
            isLoggedIn={canRespond}
            hasFetchedAnswers={hasFetchedAnswers}
            error={error}
            label="Matching"
            title={
              getSubActivityInstructionAsText(
                subActivityProperties?.instructions
              ) || properties.interactiveResponse.title
            }
            onStart={() => {
              openModal();
              startGame();
            }}
          />
        </InteractiveResponseContainer>
      </SubActivityView>
      {isVisible && (
        <ActivitySheet
          isVisible
          title={properties.interactiveResponse.title || ''}
          hasBackgroundColor={tab === QUESTION_VIEW}
          customActions={
            <ActivitySheetSaveStatus
              title="Interactive response"
              showStatus={tab === QUESTION_VIEW}
              onClose={() => {
                if (closeModalCallbackRef.current) {
                  closeModalCallbackRef.current();
                  closeModalCallbackRef.current = undefined;
                } else {
                  setTab(INDIVIDUAL_VIEW);
                  closeModal();
                }
              }}
            />
          }
          tabsContent={
            <ClassroomAnswerTabs
              isPublished={canRespond}
              includeIndividualAnswers
              includeSuggestedAnswers={showSuggestedAnswers}
              includeClassResponse={!isFreeResponse}
              tab={tab}
              onSelect={option => setTab(option)}
              componentId={id}
              onCloseActivitySheet={() => {
                if (closeModalCallbackRef.current) {
                  closeModalCallbackRef.current();
                  closeModalCallbackRef.current = undefined;
                } else {
                  setTab(INDIVIDUAL_VIEW);
                  closeModal();
                }
              }}
            />
          }
        >
          {tab === QUESTION_VIEW && (
            <>
              {isMediaUpSm ? (
                <InteractiveSequenceLargeView
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  onAnswerAttempt={handleSaveAttempt}
                  showAnswer={isLesson}
                  attemptAnswers={
                    componentAnswers?.[0]
                      ?.answer as InteractiveResponseAnswerFragment
                  }
                  setCloseModalCallback={callback => {
                    closeModalCallbackRef.current = callback;
                  }}
                />
              ) : (
                <InteractiveSequenceSmallView
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  playClick={playClick}
                  playStart={playStart}
                  playAudio={playAudio}
                  toggleAudio={toggleAudio}
                  onAnswerAttempt={handleSaveAttempt}
                  showAnswer={isLesson}
                  attemptAnswers={
                    componentAnswers?.[0]
                      ?.answer as InteractiveResponseAnswerFragment
                  }
                />
              )}
            </>
          )}
          {tab === CLASS_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetInnerMax>
                <InteractiveResponseClassResponses
                  {...props}
                  componentId={id}
                />
              </ActivitySheetInnerMax>
            </ActivitySheetInnerContent>
          )}
          {tab === INDIVIDUAL_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetGridMax>
                <InteractiveResponseIndividualResponses
                  componentId={id}
                  component={props}
                  renderAttempt={({ selectedAnswers }) =>
                    selectedAnswers ? (
                      <InteractiveResponseSequencePreviewAnswer
                        {...props}
                        attemptAnswers={selectedAnswers}
                        showAnswer
                      />
                    ) : (
                      <ClassroomAwaitingResponse />
                    )
                  }
                />
              </ActivitySheetGridMax>
            </ActivitySheetInnerContent>
          )}
          {tab === SUGGESTED_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetInnerMax>
                <InteractiveResponseSuggestedAnswers
                  {...props}
                  component={props}
                />
              </ActivitySheetInnerMax>
            </ActivitySheetInnerContent>
          )}
        </ActivitySheet>
      )}
    </>
  );
};

export default InteractiveResponseView;
